import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';

export default function Home() {
  return (
    <div className="page">

      {/* ── HERO ── */}
      <section className="hero">
        <div className="container">
          <div className="red-line" />
          <span className="eyebrow">B2B Outbound & AI Automation Firm</span>
          <h1>
            Growth Engineering<br />
            <span className="h1-dim">for Enterprise B2B.</span>
          </h1>
          <p className="lead" style={{ marginTop: 28 }}>
            We build custom outbound sales engines and AI-driven automation workflows that scale revenue with precision. For founders and operators who prioritize predictability over hype.
          </p>
          <div className="hero-buttons">
            <Link to="/contact" className="btn-red">Book a Strategy Call <ArrowRight size={14} /></Link>
            <Link to="/contact" className="btn-outline">Get in Touch</Link>
          </div>
        </div>
      </section>


      {/* ── WHAT WE DO ── */}
      <section style={{ borderBottom: '1px solid var(--border)' }}>
        <div className="container">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 80, alignItems: 'start' }}>
            <div>
              <div className="red-line" />
              <span className="eyebrow">What We Do</span>
              <h2>We build the systems that fill your pipeline.</h2>
            </div>
            <div>
              <p style={{ color: 'var(--gray-mid)', lineHeight: 1.9, marginBottom: 24 }}>
                Most B2B companies struggle with inconsistent outbound, poor targeting, and sales teams operating without a repeatable process. We fix that — systematically.
              </p>
              <p style={{ color: 'var(--gray-mid)', lineHeight: 1.9 }}>
                From cold outbound infrastructure to AI-powered follow-up sequences, we engineer every touchpoint to convert. No guesswork. No vanity metrics. Just qualified pipeline.
              </p>
              <Link to="/services" className="btn-outline" style={{ marginTop: 32, display: 'inline-flex' }}>
                View Services <ArrowRight size={14} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ── SERVICES PREVIEW ── */}
      <section style={{ borderBottom: '1px solid var(--border)' }}>
        <div className="container">
          <div style={{ marginBottom: 56 }}>
            <div className="red-line" />
            <span className="eyebrow">Core Services</span>
            <h2>Everything you need to scale outbound.</h2>
          </div>
          <div className="grid-3">
            {[
              {
                title: 'Outbound Sales Systems',
                desc: 'End-to-end outbound engine — ICP targeting, messaging, sequencing, and tech stack — built to generate consistent qualified meetings.',
                items: ['ICP Research & Targeting', 'Multi-channel Sequences', 'Cold Email Infrastructure', 'LinkedIn Outreach']
              },
              {
                title: 'AI Automation Workflows',
                desc: 'We automate the repetitive parts of your sales and ops process so your team can focus on closing deals, not managing tools.',
                items: ['Lead Enrichment Automation', 'CRM Auto-update', 'Follow-up Sequences', 'Reporting Dashboards']
              },
              {
                title: 'Sales Infrastructure',
                desc: 'Build the foundation — CRM setup, tracking, playbooks, and hiring frameworks — so your team scales without breaking.',
                items: ['CRM Implementation', 'Sales Playbooks', 'KPI Dashboards', 'Hiring Frameworks']
              }
            ].map((s, i) => (
              <div key={i} className="service-card">
                <div className="service-icon" />
                <h3>{s.title}</h3>
                <p>{s.desc}</p>
                <ul className="service-list">
                  {s.items.map((item, j) => <li key={j}>{item}</li>)}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── PROCESS ── */}
      <section style={{ borderBottom: '1px solid var(--border)' }}>
        <div className="container">
          <div style={{ marginBottom: 56 }}>
            <div className="red-line" />
            <span className="eyebrow">How It Works</span>
            <h2>From audit to pipeline in 90 days.</h2>
          </div>
          {[
            { n: '01', title: 'Discovery & Audit', desc: 'We dig into your current sales process, ICP, messaging, and tech stack. We identify the gaps killing your pipeline.' },
            { n: '02', title: 'Strategy & Build', desc: 'We design your custom outbound system — sequences, targeting, tooling — and build it out in full.' },
            { n: '03', title: 'Launch & Optimize', desc: 'We go live, track every metric, and optimize continuously. You see results in the first 30 days.' },
            { n: '04', title: 'Scale', desc: 'Once the engine is working, we help you scale it — more channels, more volume, more pipeline.' }
          ].map((step, i) => (
            <div key={i} className="process-step">
              <div className="process-number">{step.n}</div>
              <div>
                <h3 style={{ marginBottom: 12 }}>{step.title}</h3>
                <p style={{ color: 'var(--gray-mid)', fontSize: 15 }}>{step.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── CTA BANNER ── */}
      <section style={{ background: 'var(--red)', padding: '80px 0' }}>
        <div className="container" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 32 }}>
          <div>
            <h2 style={{ color: 'var(--white)', marginBottom: 12 }}>Ready to build your outbound engine?</h2>
            <p style={{ color: 'rgba(255,255,255,0.75)', fontSize: 15 }}>Book a free 30-minute strategy call. No pitch. Just clarity.</p>
          </div>
          <Link to="/contact" className="btn-outline" style={{ borderColor: 'rgba(255,255,255,0.4)', color: 'var(--white)', flexShrink: 0 }}>
            Book a Strategy Call <ArrowRight size={14} />
          </Link>
        </div>
      </section>

    </div>
  );
}
